import { AudioLines, Bookmark, Braces, Eye, Files, PanelTop, PartyPopper, Play, Presentation, Regex, Scan, Sparkles, SpellCheck, Type, X, type LucideIcon } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { useSession } from "../lib/app";
import { CompileDemo, ConfettiDemo, PlaceDemo, ReadAloudDemo, SpellDemo, type Demo } from "./WhatsNewDemos";
import "./whatsnew.css";

interface Feature {
  icon: LucideIcon;
  title: string;
  text: string;
  /** A little film of it working, which plays while the feature is picked. */
  demo?: Demo;
}

interface Release {
  /** Saved as whatsNewSeen once the card has popped up for it. */
  id: string;
  name: string;
  /** When it comes out, with Adelaide's offset (+10:30 in summer, +09:30 in winter): the same moment for everyone. */
  out: string;
  features: Feature[];
}

/** Newest first. Add a release and every account sees the card once more, from the day it comes out. */
const RELEASES: Release[] = [
  {
    id: "2026-10-2",
    name: "October 2026 · Patch 2",
    out: "2026-10-28T08:00+10:30",
    features: [
      {
        icon: AudioLines,
        title: "Read aloud",
        text: "Hear your writing read back, with each word highlighted as it's said. Typing pauses it so you can fix what you heard.",
        demo: ReadAloudDemo,
      },
      {
        icon: SpellCheck,
        title: "Better spell check",
        text: "Spell check knows Australian English: American spellings show in blue with the Australian one first, and LaTeX, citations and your sources' author names no longer get flagged.",
        demo: SpellDemo,
      },
      {
        icon: Bookmark,
        title: "Keeps your place",
        text: "Refresh, open History or switch to PDF only, and each file comes back where you were reading, cursor and all. It follows you to other devices too.",
        demo: PlaceDemo,
      },
      {
        icon: Play,
        title: "See and stop compiles",
        text: "Click Recompile while it works to see each compile. Stop one, or restart it with your latest edits. A dot means the PDF is behind.",
        demo: CompileDemo,
      },
      {
        icon: PartyPopper,
        title: "Confetti for everyone",
        text: "Press the party popper and confetti falls on everyone with a project open, with your name at the top. Everyone gets one an hour.",
        demo: ConfettiDemo,
      },
    ],
  },
  {
    id: "2026-10",
    name: "October 2026 · Patch 1",
    out: "2026-10-01T00:00+09:30",
    features: [
      { icon: PanelTop, title: "Menu bar", text: "File, Edit, Insert, View and Format, with shortcuts." },
      { icon: Type, title: "Formatting toolbar", text: "Headings, lists, maths, symbols, figures and tables in a click." },
      { icon: Eye, title: "Visual editor", text: "See headings, maths and figures as you type." },
      { icon: Files, title: "Tabs", text: "Keep several files open. They follow you to other devices." },
      { icon: Regex, title: "Find and replace", text: "Regex, match case, whole word, or just the selection." },
      { icon: Presentation, title: "PDF viewer", text: "Fit to width, present full screen, or read it in dark mode." },
      { icon: Scan, title: "Focus mode", text: "Hide the panels and just write." },
      { icon: Braces, title: "Code highlighting", text: "Python, MATLAB, R and 30+ more languages." },
    ],
  },
];

/** In development, ?whatsnew=all shows releases before they're out. */
const isOut = (r: Release) =>
  Date.now() >= Date.parse(r.out) || (import.meta.env.DEV && new URLSearchParams(location.search).get("whatsnew") === "all");

const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * The "What's new" button beside Review, and the card it opens. The card pops up by itself once
 * per account for each release; closing it shrinks it into the button, so people see where it went.
 * Earlier releases get greyed-out cards below it, the first peeking up from the bottom of the screen.
 */
export function WhatsNew() {
  const { user, updateSettings } = useSession();
  // Worked out on arrival: a release that comes out while a project is open shows up on the next one.
  const [releases] = useState(() => RELEASES.filter(isOut));
  const [latest, ...earlier] = releases;
  // "pop" when it opens by itself, "grow" when it grows out of the button.
  const [open, setOpen] = useState<"pop" | "grow" | null>(null);
  const [landed, setLanded] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const layer = useRef<HTMLDivElement>(null);
  const stack = useRef<HTMLDivElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const blob = useRef<HTMLDivElement>(null);
  const scrim = useRef<HTMLDivElement>(null);
  const ok = useRef<HTMLButtonElement>(null);
  const closing = useRef(false);

  useEffect(() => {
    if (!user || !latest || user.settings.whatsNewSeen === latest.id) return;
    const t = window.setTimeout(() => {
      setOpen("pop");
      updateSettings({ whatsNewSeen: latest.id }).catch(() => {});
    }, 900);
    return () => window.clearTimeout(t);
    // Only on arrival: marking it seen mustn't cancel the card that's showing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Where the button is from the card's centre, and the scale that shrinks the card to its size. */
  const towardButton = () => {
    const b = button.current?.getBoundingClientRect();
    const c = card.current?.getBoundingClientRect();
    if (!b || !c || !b.width) return null;
    return `translate(${b.left + b.width / 2 - (c.left + c.width / 2)}px, ${b.top + b.height / 2 - (c.top + c.height / 2)}px) scale(${b.width / c.width})`;
  };

  useLayoutEffect(() => {
    const el = card.current;
    if (!open || !el) return;
    ok.current?.focus({ preventScroll: true });
    if (reducedMotion()) return;
    const from = open === "grow" ? towardButton() : null;
    if (from) {
      const timing = { duration: 480, easing: "cubic-bezier(0.22, 1, 0.36, 1)" };
      el.animate([{ transform: from, borderRadius: "50%" }, { transform: "translate(0px, 0px) scale(1)", borderRadius: "16px" }], timing);
      blob.current?.animate([{ opacity: 1 }, { opacity: 1, offset: 0.25 }, { opacity: 0, offset: 0.7 }, { opacity: 0 }], timing);
    } else {
      el.animate([{ transform: "translateY(14px) scale(0.96)", opacity: 0 }, { transform: "none", opacity: 1 }], { duration: 380, easing: "cubic-bezier(0.34, 1.4, 0.64, 1)" });
    }
  }, [open]);

  // Centre the card, unless there are earlier releases: then sit it high enough that the first of
  // them starts a quarter on screen, three-quarters off the bottom.
  useLayoutEffect(() => {
    const el = layer.current;
    const main = card.current;
    if (!open || !el || !main) return;
    const next = main.nextElementSibling;
    const place = () => {
      const centred = (el.clientHeight - main.offsetHeight) / 2;
      const peeking = next instanceof HTMLElement ? el.clientHeight - next.offsetHeight / 4 - (next.offsetTop - main.offsetTop) : centred;
      el.style.setProperty("--top", `${Math.max(16, Math.min(centred, peeking))}px`);
    };
    place();
    const watch = new ResizeObserver(place);
    for (const n of [el, main, next]) if (n) watch.observe(n);
    return () => watch.disconnect();
  }, [open]);

  const close = useCallback(() => {
    const el = card.current;
    if (!el || closing.current) return;
    closing.current = true;
    const done = () => {
      closing.current = false;
      setOpen(null);
      button.current?.focus({ preventScroll: true });
    };
    // Finish the opening animations so the card flies off whole. The demos' endless ones can't finish.
    stack.current?.getAnimations({ subtree: true }).forEach((a) => a.effect?.getComputedTiming().endTime !== Infinity && a.finish());
    const to = reducedMotion() ? null : towardButton();
    if (!to) return done();
    // A small step back, then the card turns into a green dot and flies into the button.
    const timing = { duration: 560, easing: "cubic-bezier(0.55, 0, 0.2, 1)", fill: "forwards" as const };
    const flight = el.animate(
      [
        { transform: "translate(0px, 0px) scale(1)", borderRadius: "16px" },
        { transform: "translate(0px, 0px) scale(0.94)", borderRadius: "20px", offset: 0.2 },
        { transform: to, borderRadius: "50%" },
      ],
      timing,
    );
    blob.current?.animate([{ opacity: 0 }, { opacity: 0, offset: 0.12 }, { opacity: 1, offset: 0.5 }, { opacity: 1 }], timing);
    scrim.current?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 420, fill: "forwards" });
    // The earlier releases sink away as it goes.
    for (const other of stack.current?.children ?? []) {
      if (other !== el) other.animate([{ opacity: 0, transform: "translateY(40px)" }], { duration: 300, easing: "ease-in", fill: "forwards" });
    }
    flight.finished.then(() => {
      done();
      // The button bounces, as if it had caught the card.
      setLanded(true);
    }, done);
  }, []);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      close();
    } else if (e.key === "Tab") {
      // Keep focus inside the cards.
      const stops = [...(stack.current?.querySelectorAll<HTMLElement>("button") ?? [])].filter((b) => !b.closest("[inert]"));
      const i = stops.indexOf(document.activeElement as HTMLElement);
      const next = stops[(i + (e.shiftKey ? stops.length - 1 : 1)) % stops.length];
      if (next) {
        e.preventDefault();
        next.focus();
      }
    }
  };

  if (!latest) return null;

  return (
    <>
      <button
        ref={button}
        className={`icon-btn wn-btn${landed ? " landed" : ""}`}
        onClick={() => (open ? close() : setOpen("grow"))}
        onAnimationEnd={(e) => e.animationName === "wn-ripple" && setLanded(false)}
        title="What's new"
        aria-label="What's new"
        aria-haspopup="dialog"
      >
        <Sparkles size={16} />
      </button>
      {open &&
        createPortal(
          // A click beside the cards closes them, like one on the scrim.
          <div ref={layer} className="wn-layer" onKeyDown={onKeyDown} onClick={(e) => (e.target === layer.current || e.target === stack.current) && close()}>
            <div className="wn-scrim" ref={scrim} />
            <div ref={stack} className="wn-stack" role="dialog" aria-modal="true" aria-labelledby="wn-title">
              <div ref={card} className={`wn-card wn-${open}`}>
                <header className="wn-head">
                  <span className="wn-eyebrow">
                    <Sparkles size={12} /> {latest.name}
                  </span>
                  <h2 id="wn-title">
                    What's new in <em>benleaf</em>
                  </h2>
                  <button className="icon-btn wn-x" onClick={close} aria-label="Close">
                    <X size={16} />
                  </button>
                </header>
                <div className="wn-body">
                  {latest.features.every((f) => f.demo) ? <Showcase features={latest.features} /> : <Features features={latest.features} />}
                </div>
                <footer className="wn-foot">
                  <span>
                    Find this again under <Sparkles size={13} /> next to Review.
                  </span>
                  <button ref={ok} className="btn btn-primary" onClick={close}>
                    Got it
                  </button>
                </footer>
                <div ref={blob} className="wn-blob" aria-hidden="true" />
              </div>
              {earlier.map((r) => (
                <section key={r.id} className="wn-card wn-earlier" aria-label={r.name}>
                  <header className="wn-head">
                    <span className="wn-eyebrow">{r.name}</span>
                    <h2>
                      Earlier in <em>benleaf</em>
                    </h2>
                  </header>
                  <div className="wn-body">
                    <Features features={r.features} />
                  </div>
                </section>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/** A release's features as a plain list. */
function Features({ features }: { features: Feature[] }) {
  return (
    <ul className="wn-list">
      {features.map((f, i) => (
        <li key={f.title} style={{ "--i": i } as CSSProperties}>
          <span className="wn-icon">
            <f.icon size={16} />
          </span>
          <span>
            <b>{f.title}</b>
            {f.text}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** A release with demos: the picked feature's demo plays, then the next one's, round and round. */
function Showcase({ features }: { features: Feature[] }) {
  const still = reducedMotion();
  const [at, setAt] = useState(0);
  // Picking the feature that's on again replays its demo.
  const [run, setRun] = useState(0);
  const feature = features[at];
  const Film = feature.demo!;

  useEffect(() => {
    if (still) return;
    const t = window.setTimeout(() => setAt((i) => (i + 1) % features.length), Film.duration);
    return () => window.clearTimeout(t);
  }, [at, run, still, Film.duration, features.length]);

  return (
    <div className="wn-show">
      <Film key={`${at}.${run}`} still={still} />
      <div className="wn-feats" role="tablist" aria-label="Features">
        {features.map((f, i) => (
          <button
            key={f.title}
            role="tab"
            aria-selected={i === at}
            className={`wn-feat${i === at ? " on" : ""}`}
            style={{ "--i": i, "--length": `${f.demo!.duration}ms` } as CSSProperties}
            onClick={() => (i === at ? setRun((n) => n + 1) : setAt(i))}
          >
            {/* Fills while the demo plays, then the next feature's starts. */}
            {i === at && !still && <span key={run} className="wn-feat-bar" aria-hidden="true" />}
            <f.icon size={14} />
            {f.title}
          </button>
        ))}
      </div>
      <p key={at} className="wn-about" role="tabpanel" aria-label={feature.title}>
        {feature.text}
      </p>
    </div>
  );
}
